import React, { useState, useEffect, version } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  UserCircleIcon,
  UsersIcon,
  UserGroupIcon,
  BriefcaseIcon,
  TruckIcon,
  IdentificationIcon,
  CalendarDaysIcon,
  CurrencyRupeeIcon,
  ChartBarSquareIcon,
  ClipboardDocumentListIcon,
  WrenchScrewdriverIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline';
import { logout, USER_PROFILE, USER_ROLE } from '../services/auth';
import { useOverallCount } from '../context/OverallCountContext';
import { useVersionChecker } from '../context/app_version';
import { useUnsavedChanges } from "../context/UnsavedChangesContext";


const Sidebar = () => {

  const [isOpen, setIsOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [badges, setBadges] = useState({
    trip: 0,
    vehicle_expenses: 0,
    driver_expenses: 0
  });
  const {overallCount, loading, refresh: refreshOverallCount} = useOverallCount({ reload: false });
  const { isNew, currentVersion } = useVersionChecker();
  const { confirmIfDirty } = useUnsavedChanges();

  const navigate = useNavigate();

  useEffect(() => {
    const role = localStorage.getItem(USER_ROLE);
    const profile = JSON.parse(localStorage.getItem(USER_PROFILE));
    setUserRole(role);
    setUserProfile(profile);
  }, []);

  useEffect(() => {
    updateBadge();
  }, [overallCount]);

  const menuItems = [
    {
      label: 'DASHBOARD',
      path: '/dashboard',
      icon: HomeIcon,
      roles: ['admin', 'branch_admin', 'branch_user'],
      badge: badges.vehicle_expenses + badges.driver_expenses,
    },
    {
      label: 'BRANCH',
      icon: BuildingOffice2Icon,
      submenu: true,
      expanded: false,
      roles: ['admin', 'branch_admin'], // Only visible to superadmin
      children: [
        { label: 'BRANCHS', path: '/branches', icon: BuildingOfficeIcon, roles: ['admin'] },
        { label: "BRANCH ADMIN'S", path: '/branch-admins', icon: UserCircleIcon, roles: ['admin'] },
        { label: "BRANCH USER'S", path: '/branch-users', icon: UsersIcon, roles: ['admin', 'branch_admin'] },
      ],
    },
    { label: 'CUSTOMERS', path: '/customers', icon: UserGroupIcon, roles: ['admin'] },
    { label: 'CLIENTS', path: '/clients', icon: BriefcaseIcon, roles: ['admin'] },
    { label: 'TRIPS', path: '/trips', icon: TruckIcon, roles: ['admin', 'branch_admin', 'branch_user'], badge: badges.trip },
    {
      label: 'DRIVERS MANAGEMENT',
      path: '/drivers',
      icon: IdentificationIcon,
      roles: ['admin', 'branch_admin', 'branch_user'],
    },
    {
      label: 'VEHICLES MANAGEMENT',
      path: '/vehicles',
      icon: TruckIcon,
      roles: ['admin', 'branch_admin', 'branch_user'],
    },
    { label: 'ATTENDANCE', path: '/attendance', icon: CalendarDaysIcon, roles: ['admin', 'branch_admin'] },
    {
      label: 'EXPENSES',
      icon: CurrencyRupeeIcon,
      submenu: true,
      expanded: false,
      roles: ['admin', 'branch_admin'], // Only visible to branchadmin, superadmin
      badge: (badges.vehicle_expenses + badges.driver_expenses),
      children: [
        { label: 'DRIVER', path: '/driver-expenses', icon: IdentificationIcon, roles: ['admin', 'branch_admin', 'branch_user'], badge: badges.driver_expenses },
        { label: "VEHICLE", path: '/vehicle-expenses', icon: WrenchScrewdriverIcon, roles: ['admin', 'branch_admin', 'branch_user'], badge: badges.vehicle_expenses },
      ],
    },
    {
      label: 'REPORTS',
      icon: ChartBarSquareIcon,
      submenu: true,
      expanded: false,
      roles: ['admin', 'branch_admin', 'branch_user'],
      children: [
        { label: 'TRIPS', path: '/reports/trips', icon: ClipboardDocumentListIcon, roles: ['admin', 'branch_admin', 'branch_user'] },
        { label: 'VEHICLE EXPENSE', path: '/reports/vehicle-expense', icon: WrenchScrewdriverIcon, roles: ['admin', 'branch_admin', 'branch_user'] },
        { label: 'DRIVER EXPENSE', path: '/reports/driver-expense', icon: IdentificationIcon, roles: ['admin', 'branch_admin', 'branch_user'] },
        { label: 'ATTENDANCE', path: '/reports/attendance', icon: PresentationChartLineIcon, roles: ['admin', 'branch_admin'] },
      ],
    },
    { label: 'PROFILE', path: '/admin/profile', icon: UserCircleIcon, roles: ['admin'] },
    { label: 'PROFILE', path: '/profile', icon: UserCircleIcon, roles: ['branch_admin', 'branch_user'] },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleExpand = (label) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const onNavigate = async (e, path) => {
    if (location.pathname === path) return;
    e.preventDefault();
    refreshOverallCount();
    updateBadge();
    confirmIfDirty(() => navigate(path));
  };

  const updateBadge = () => {
    setBadges({
      trip: overallCount.ongoing_trips_count || 0,
      vehicle_expenses: overallCount.pending_vehicle_expenses_count || 0,
      driver_expenses: overallCount.pending_driver_expenses_count || 0,
    });
  };

  const renderMenuItems = (isMobile = false) => (
    <>
      {menuItems.map(item => {
        if (!item.roles.includes(userRole)) return null;

        if (item.submenu) {
          const visibleChildren = item.children.filter(child => child.roles.includes(userRole));

          if (visibleChildren.length === 0) return null;

          const isExpanded = expandedMenus[item.label] || false;

          return (
            <div key={item.label}>
              <NavLink
                onClick={() => toggleExpand(item.label)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-100"
              >
                <span className="flex items-center gap-2">
                  {item.icon && <item.icon className="h-5 w-5" />}
                  <span className="whitespace-nowrap">{item.label}</span>
                </span>
                <span className="flex items-center gap-2">
                  {item.badge > 0 && (
                    <span className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold min-w-[20px] h-5 px-2">
                      {item.badge}
                    </span>
                  )}
                  {isExpanded ? (
                    <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                  )}
                </span>
              </NavLink>
              {isExpanded && (
                <div className="ml-4 space-y-1">
                  {visibleChildren.map(child => (
                    <NavLink
                      key={child.path}
                      to={child.path}
                      onClick={ isMobile ? () => setIsOpen(false) : (e) => onNavigate(e, child.path)}
                      className={({ isActive }) =>
                        `block px-3 py-2 rounded-md text-xs font-medium ${
                          isActive ? 'bg-blue-100 text-blue-600' : 'text-gray-700 hover:bg-gray-100'
                        }`
                      }
                    >
                      <span className="flex justify-between items-center w-full">
                        <span className="flex items-center gap-2">
                          {child.icon && <child.icon className="h-4 w-4" />}
                          <span className="whitespace-nowrap">{child.label}</span>
                        </span>
                        {child.badge > 0 && (
                          <span className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold min-w-[20px] h-5 px-2">
                            {child.badge}
                          </span>
                        )}
                      </span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        } else {
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={isMobile ? () => setIsOpen(false) : (e) => onNavigate(e, item.path)}
              className={({ isActive }) =>
                `block px-3 py-2 rounded-md text-xs font-medium ${
                  isActive ? 'bg-blue-100 text-blue-600' : 'text-gray-700 hover:bg-gray-100'
                }`
              }
            >
              <span className="flex justify-between items-center w-full">
                  <span className="flex items-center gap-2">
                    {item.icon && <item.icon className="h-5 w-5" />}
                    <span className="whitespace-nowrap">{item.label}</span>
                  </span>
                  {item.badge > 0 && (
                    <span className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold min-w-[20px] h-5 px-2">
                      {item.badge}
                    </span>
                  )}
                </span>
            </NavLink>
          );
        }
      })}
    </>
  );

  return (
    <>
      {/* Top Navbar (mobile only) */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white shadow flex items-center justify-between px-4 h-14 border-b border-gray-200">
        <img src="/logo_rapidelo.png" alt="Logistics Logo" className="h-8 w-auto" />
        <div className="flex flex-col items-center leading-tight">
          <p className="text-sm uppercase font-bold text-[#293686]">{userProfile?.name}</p>
          <p className="text-xs uppercase font-semibold text-[#293686]">{userProfile?.branch?.name}</p>
        </div>
        <button onClick={() => setIsOpen(!isOpen)} className="relative">
          {isOpen ? (
            <XMarkIcon className="h-6 w-6 text-gray-800" />
          ) : (
            <>
              <Bars3Icon className="h-6 w-6 text-gray-800" />
              {(badges.vehicle_expenses + badges.driver_expenses) > 0 && (
                <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold min-w-[16px] h-4 px-1">
                  {badges.vehicle_expenses + badges.driver_expenses}
                </span>
              )}
            </>
          )}
        </button>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-60 h-screen fixed left-0 top-0 bg-white border-r border-gray-200 shadow-sm">
        <div className="p-5 border-b border-gray-100 flex flex-col items-center">
          <img src="/logo_rapidelo.png" alt="Logistics Logo" className="h-24 w-auto" />
          <p className="text-lg uppercase font-bold text-center text-[#293686] mt-2">{userProfile?.name || userRole}</p>
          <p className="text-lg uppercase font-bold text-center text-[#293686] mt-2">{userProfile?.branch?.name}</p>
        </div>
        <div className="flex-1 flex flex-col justify-between overflow-y-auto">
          <nav className="p-4 space-y-2">{renderMenuItems()}</nav>
          <div className="p-4 border-t border-gray-100">
            <p className="text-sm text-gray-500 mb-2 text-center uppercase">{userRole}</p>
            <p className="text-sm text-gray-500 mb-2 text-center">Version {currentVersion}</p>
            <button
              onClick={handleLogout}
              className="w-full bg-red-500 text-white text-sm py-2 rounded hover:bg-red-600 transition"
            >
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Fullscreen */}
      {isOpen && (
        <div className="md:w-64 w-full md:relative fixed inset-0 z-50 bg-white shadow-lg md:shadow-none">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Rapidelo</h2>
            <button onClick={() => setIsOpen(false)}>
              <XMarkIcon className="h-6 w-6 text-gray-800" />
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">{renderMenuItems(true)}</nav>

          <div className="p-4 border-t border-gray-200 flex flex-col items-center">
            <p className="text-sm text-gray-500 mb-2">Version {currentVersion}</p>
            <p className="text-sm text-gray-500 mb-2">Version {currentVersion}</p>
            <button
              onClick={handleLogout}
              className="bg-red-500 text-white text-sm px-5 py-2 rounded hover:bg-red-600 transition"
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
